import {
  ExecutionStep, StepType, ArrayItem, Vertex, Edge, GraphMetadata,
  ArrayMetadata, MatrixMetadata, TreeNode, TreeEdge, TreeMetadata,
  GraphStatePayload, ArrayStatePayload, MatrixStatePayload, TreeStatePayload,
  StringStatePayload, StringMetadata, GenericStatePayload,
} from '../types/rust-engine-interfaces';

declare module '../types/rust-engine-interfaces' {
  interface TreeMetadata {
    highlightedEdges?: TreeEdge[];
    rotation?: 'LL' | 'RR' | 'LR' | 'RL';
    balanceFactor?: Record<string, number>;
    color?: 'red' | 'black';
    splayStep?: 'zig' | 'zig-zig' | 'zig-zag';
    priority?: number;
    key?: number;
    heapAction?: 'sift-up' | 'sift-down' | 'merge' | 'link' | 'merge-pairs' | 'decrease-key';
    swappedNodes?: string[];
    degree?: number;
    npl?: number;
    isLeaf?: boolean;
    keys?: number[];
    splitIndex?: number;
    range?: [number, number];
  }
}

export type Payload = GraphStatePayload | ArrayStatePayload | MatrixStatePayload | TreeStatePayload | StringStatePayload | GenericStatePayload;

export function unwrapInput(input: unknown): unknown {
  if (!input) return {};
  if (typeof input === 'object' && 'data' in input) return (input as { data: unknown }).data;
  return input;
}

export function step(idx: number, type: StepType, payload: Payload, meta?: { line?: number; explanation?: string; highlightIds?: string[] }): ExecutionStep {
  return { stepId: idx, stepType: type, timestamp: Date.now() + idx, payload, metadata: meta ?? {} };
}

export function arrayPayload(arr: number[], meta: ArrayMetadata): ArrayStatePayload {
  const items: ArrayItem[] = arr.map((v, i) => ({
    index: i, value: v,
    highlighted: meta.highlightIndices?.includes(i) ?? false,
    compared: meta.compareIndices?.includes(i) ?? false,
    swapped: meta.currentIndex === i && meta.compareIndex !== i,
    sorted: meta.sortedIndices?.includes(i) ?? false,
  }));
  return { type: 'array', items, metadata: meta };
}

export function matrixPayload(data: number[][], meta: MatrixMetadata): MatrixStatePayload {
  return { type: 'matrix', rows: data.length, cols: data[0]?.length ?? 0, data, metadata: meta };
}

export function graphPayload(v: Vertex[], e: Edge[], m: GraphMetadata): GraphStatePayload {
  return { type: 'graph', vertices: v, edges: e, metadata: m };
}

export function treePayload(nodes: TreeNode[], edges: TreeEdge[], m: TreeMetadata): TreeStatePayload {
  return { type: 'tree', nodes, edges, metadata: m };
}

export function stringPayload(s: string, m: Partial<StringMetadata>): StringStatePayload {
  return { type: 'string', value: s, metadata: m };
}

export function genericPayload(elements: unknown[], extra: Record<string, unknown> = {}): GenericStatePayload {
  return { type: 'generic', data: { elements, ...extra }, metadata: {} };
}

export function normalizeArray(raw: unknown): number[] {
  const fallback = [64, 34, 25, 12, 22, 11, 90, 88, 45, 50].slice(0, 10);
  const pick = (src: unknown[]): number[] => {
    const result = src.filter((v): v is number => typeof v === 'number').slice(0, 10);
    return result.length > 0 ? result : fallback;
  };
  if (Array.isArray(raw)) return pick(raw);
  if (raw && typeof raw === 'object') {
    const r = raw as Record<string, unknown>;
    if (Array.isArray(r.data)) return pick(r.data);
    if (Array.isArray(r.arr)) return pick(r.arr);
    if (Array.isArray(r.array)) return pick(r.array);
  }
  return fallback;
}

export function normalizeGraph(raw: unknown): { vertices: Vertex[]; edges: Edge[]; directed: boolean; weighted: boolean; start: string } {
  const data = (
    raw && typeof raw === 'object'
      ? (raw as { type?: string; graph?: Record<string, unknown> }).type === 'graph'
        ? (raw as Record<string, unknown>)
        : (raw as Record<string, unknown>).graph ?? (raw as Record<string, unknown>)
      : {}
  ) as Record<string, unknown>;
  const rawVertices = Array.isArray(data.vertices) ? data.vertices : Array.isArray(data.nodes) ? data.nodes : [];
  const rawEdges = Array.isArray(data.edges) ? data.edges : [];
  const vertices: Vertex[] = (rawVertices as unknown[]).map((v, i) => {
    const vo = v as Record<string, unknown>;
    return { id: String(vo.id ?? i), value: String(vo.value ?? vo.id ?? i), label: String(vo.label ?? vo.id ?? i) };
  });
  const edges: Edge[] = (rawEdges as unknown[]).map((e) => {
    const eo = e as Record<string, unknown>;
    return { from: String(eo.from), to: String(eo.to), weight: typeof eo.weight === 'number' ? eo.weight : undefined };
  });
  const directed = !!data.directed;
  const weighted = edges.some((e) => e.weight !== undefined);
  return { vertices, edges, directed, weighted, start: String(data.startNode ?? data.startVertex ?? vertices[0]?.id ?? 'A') };
}

export function adjacency(g: ReturnType<typeof normalizeGraph>) {
  const map = new Map<string, { to: string; weight: number }[]>();
  g.vertices.forEach((v) => map.set(v.id, []));
  g.edges.forEach((e) => { map.get(e.from)?.push({ to: e.to, weight: e.weight ?? 1 }); if (!g.directed) map.get(e.to)?.push({ to: e.from, weight: e.weight ?? 1 }); });
  return map;
}

export function rangeIds(start: number, end: number): number[] {
  const ids: number[] = [];
  for (let i = start; i <= end; i++) ids.push(i);
  return ids;
}

export function genericFallback(id: string, raw: unknown, category: string): ExecutionStep[] {
  const cat = category.toLowerCase().trim();

  // Graph/flow/network → always produce graph payloads
  if (cat === 'graph' || cat === 'graphs' || cat === 'flow' || cat === 'network' || cat === 'network-flow') {
    const g = normalizeGraph(raw);
    const s: ExecutionStep[] = [];
    s.push(step(s.length, StepType.Initialization, graphPayload(g.vertices, g.edges, { directed: g.directed, weighted: g.weighted, startVertex: g.start }), { line: 1, explanation: `${id}: graph with ${g.vertices.length} vertices, ${g.edges.length} edges.` }));
    for (let i = 0; i < g.vertices.length; i++) {
      const vid = g.vertices[i]!.id;
      s.push(step(s.length, StepType.StateChange, graphPayload(g.vertices, g.edges, { directed: g.directed, weighted: g.weighted, startVertex: g.start, currentVertex: vid, visitedVertices: g.vertices.slice(0, i + 1).map(v => v.id) }), { line: 2, explanation: `${id}: visit vertex ${vid}.` }));
    }
    s.push(step(s.length, StepType.Completion, graphPayload(g.vertices, g.edges, { directed: g.directed, weighted: g.weighted, startVertex: g.start, visitedVertices: g.vertices.map(v => v.id) }), { line: 4, explanation: `${id}: graph traversal complete.` }));
    return s;
  }

  // Sorting/search/greedy/math/number-theory → always produce array payloads
  if (cat === 'sorting' || cat === 'search' || cat === 'greedy' || cat === 'math' || cat === 'number-theory' || cat === 'number theory') {
    const arr = normalizeArray(raw);
    const s: ExecutionStep[] = [];
    s.push(step(s.length, StepType.Initialization, arrayPayload([...arr], { highlightIndices: [] }), { line: 1, explanation: `${id}: processing ${arr.length} values.` }));
    for (let i = 0; i < arr.length; i++) {
      const hi = Math.min(i + 1, arr.length - 1);
      s.push(step(s.length, StepType.Comparison, arrayPayload([...arr], { currentIndex: i, compareIndex: hi, highlightIndices: [i, hi] }), { line: 2, explanation: `${id}: inspect arr[${i}] = ${arr[i]}.` }));
    }
    s.push(step(s.length, StepType.Completion, arrayPayload([...arr], { sortedIndices: arr.map((_, i) => i) }), { line: 4, explanation: `${id}: complete.` }));
    return s;
  }

  // DP/backtracking → matrix payloads (try matrix detection first)
  if (cat === 'dp' || cat === 'dynamic programming' || cat === 'dynamic-programming' || cat === 'backtracking') {
    function tryMatrix(v: unknown): number[][] | null {
      if (!Array.isArray(v)) return null;
      if (v.length === 0) return null;
      if (!Array.isArray(v[0])) return null;
      const data = (v as unknown[][]).map((row) =>
        (row as unknown[]).map((cell) => (typeof cell === 'number' ? cell : 0))
      );
      if (data.length === 0 || (data[0]?.length ?? 0) === 0) return null;
      return data;
    }

    const r = raw as Record<string, unknown>;
    const rawCandidates = [raw, r.data, r.matrix, r.grid, r.dp, r.board];
    for (const cand of rawCandidates) {
      const m = tryMatrix(cand);
      if (m) {
        const rows = m.length;
        const cols = m[0]?.length ?? 0;
        const s: ExecutionStep[] = [];
        s.push(step(s.length, StepType.Initialization, matrixPayload([...m.map((r) => [...r])], {}), { line: 1, explanation: `${id}: ${rows}×${cols} matrix initialised.` }));
        for (let i = 0; i < rows; i++) {
          for (let j = 0; j < cols; j++) {
            s.push(step(s.length, StepType.Comparison, matrixPayload([...m.map((r) => [...r])], { currentCell: { row: i, col: j } }), { line: 2, explanation: `${id}: process cell (${i},${j}) = ${m[i]![j]}.` }));
          }
        }
        s.push(step(s.length, StepType.Completion, matrixPayload(m, {}), { line: 4, explanation: `${id}: matrix processing complete.` }));
        return s;
      }
    }
    // If no matrix found, fall through to generic array below
  }

  // String/automaton/compression → string payloads
  if (cat === 'string' || cat === 'strings' || cat === 'automaton' || cat === 'compression' || cat === 'cryptography') {
    const text = typeof raw === 'string' ? raw : (raw as Record<string, unknown>)?.text ?? (raw as Record<string, unknown>)?.value ?? 'algorithm';
    const s: ExecutionStep[] = [];
    s.push(step(s.length, StepType.Initialization, stringPayload(String(text), { currentIndex: 0 }), { line: 1, explanation: `${id}: processing string of length ${String(text).length}.` }));
    for (let i = 0; i < String(text).length; i++) {
      s.push(step(s.length, StepType.Comparison, stringPayload(String(text), { currentIndex: i, highlightIndices: [i] }), { line: 2, explanation: `${id}: inspect char at index ${i}.` }));
    }
    s.push(step(s.length, StepType.Completion, stringPayload(String(text), { matchedIndices: Array.from({ length: String(text).length }, (_, i) => i) }), { line: 4, explanation: `${id}: string processing complete.` }));
    return s;
  }

  // Unknown category — auto-detect matrix vs array
  function tryMatrix(v: unknown): number[][] | null {
    if (!Array.isArray(v)) return null;
    if (v.length === 0) return null;
    if (!Array.isArray(v[0])) return null;
    const data = (v as unknown[][]).map((row) =>
      (row as unknown[]).map((cell) => (typeof cell === 'number' ? cell : 0))
    );
    if (data.length === 0 || (data[0]?.length ?? 0) === 0) return null;
    return data;
  }

  const r = raw as Record<string, unknown>;
  const rawCandidates = [raw, r.data, r.matrix, r.grid, r.dp, r.board];
  for (const cand of rawCandidates) {
    const m = tryMatrix(cand);
    if (m) {
      const rows = m.length;
      const cols = m[0]?.length ?? 0;
      const s: ExecutionStep[] = [];
      s.push(step(s.length, StepType.Initialization, matrixPayload([...m.map((r) => [...r])], {}), { line: 1, explanation: `${id}: ${rows}×${cols} matrix initialised.` }));
      for (let i = 0; i < rows; i++) {
        for (let j = 0; j < cols; j++) {
          s.push(step(s.length, StepType.Comparison, matrixPayload([...m.map((r) => [...r])], { currentCell: { row: i, col: j } }), { line: 2, explanation: `${id}: process cell (${i},${j}) = ${m[i]![j]}.` }));
        }
      }
      s.push(step(s.length, StepType.Completion, matrixPayload(m, {}), { line: 4, explanation: `${id}: matrix processing complete.` }));
      return s;
    }
  }

  const nums: number[] = [];
  function harvest(v: unknown) {
    if (typeof v === 'number' && isFinite(v)) { nums.push(v); return; }
    if (Array.isArray(v)) { v.forEach(harvest); return; }
    if (v && typeof v === 'object') { Object.values(v).forEach(harvest); }
  }
  harvest(raw);
  const arr = nums.slice(0, 12).length > 0 ? nums.slice(0, 12) : [8, 3, 6, 1, 9, 4, 7, 2, 5];

  const s: ExecutionStep[] = [];
  s.push(step(s.length, StepType.Initialization, arrayPayload([...arr], { highlightIndices: [] }), { line: 1, explanation: `${id}: processing ${arr.length} values.` }));
  for (let i = 0; i < arr.length; i++) {
    const hi = Math.min(i + 1, arr.length - 1);
    s.push(step(s.length, StepType.Comparison, arrayPayload([...arr], { currentIndex: i, compareIndex: hi, highlightIndices: [i, hi] }), { line: 2, explanation: `${id}: inspect arr[${i}] = ${arr[i]}.` }));
  }
  s.push(step(s.length, StepType.Completion, arrayPayload([...arr], { sortedIndices: arr.map((_, i) => i) }), { line: 4, explanation: `${id}: complete.` }));
  return s;
}
